import { useCallback, useEffect, useRef, useState } from "react";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { ASSET_REGISTRY, EMPLOYEE_INTERN_INFO, EMPLOYEE_INTERN_SAL, EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, EMPLOYEE_GROUP, EMPLOYEE_PERSONAL_INFO, EMPLOYEE_SHIFT, EMPLOYEE_TYPE, LETTER_TEMPLATE_API, LETTER_TYPE_API, USERGROUP } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { ChevronLeft, ChevronRight, Check, FileText, User, CreditCard, Send, Save, Mail, TextQuote, Move } from 'lucide-react';
import { cn } from "../../utils/tailwindUtils";
import { useLocation, useNavigate } from "react-router-dom";
import EmployeeDetailsContent from "./helpers/EmployeeDetailsContent";
import DocumentDetailsContent from "./helpers/DocumentDetailsContent";
import SalaryDetailsContent from "./helpers/SalaryDetailsContent";
import IssuedDocumentContent from "./helpers/IssuedDocumentContent";
import FinalVerificationContent from "./helpers/FinalVerificationContent";

// Stepper functionality
const InternOnboardingViewDetailsIndex = () => {

    const { fetch } = useApiMethods();

    const location = useLocation();
    const navigate = useNavigate();

    const docRef = useRef()
    const salRef = useRef()
    const finalRef = useRef()

    const { employeeID = null, row, isActive } = location.state

    const [empCardDetails, setEmpCardDetails] = useState({
        title: "",
        first_name: "",
        last_name: "",
        designation_id: {
            id: "",
            designation_name: ""
        },
        id: "",
        profile: null,
    })
    const [employeeContentData, setEmployeeContentData] = useState({})

    const [loading, setLoading] = useState(false)
    const [screenLoader, setScreenLoader] = useState(false)
    const [dropdownData, setDropdownData] = useState({
        department: [],
        designation: [],
        empinfo: [],
        empgroup: [],
        emptype: [],
        shift: [],
        usergroup: [],
        assetreg: [],
        lettertype: [],
        letterTemp: [],
    })

    // Add a state to track if dropdowns are loaded
    const [dropdownsLoaded, setDropdownsLoaded] = useState(false);

    // common api calls to fetch the datas
    const getDepartmentList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getDesignationList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DESIGNATION, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeGroup = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_GROUP, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeType = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_TYPE, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeShift = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_SHIFT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getUserGroup = useCallback(async () => {
        try {
            const apiData = await fetch(USERGROUP, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getAssetRegistry = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_REGISTRY, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getLetterType = useCallback(async () => {
        try {
            const apiData = await fetch(LETTER_TYPE_API, { drop_down: 'True', onboarding_only: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getLetterTemplate = useCallback(async () => {
        try {
            const apiData = await fetch(LETTER_TEMPLATE_API, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const fetchDropdown = async () => {
        setScreenLoader(true)
        setLoading(true)
        const [department, designation, empinfo, empgroup, emptype, shift, usergroup, assetreg, lettertype, lettertemp] = await Promise.all([
            getDepartmentList(),
            getDesignationList(),
            getEmployeeList(),
            getEmployeeGroup(),
            getEmployeeType(),
            getEmployeeShift(),
            getUserGroup(),
            getAssetRegistry(),
            getLetterType(),
            getLetterTemplate(),
        ])

        const dropdowns = {
            department: department || [],
            designation: designation || [],
            empinfo: empinfo || [],
            empgroup: empgroup || [],
            emptype: emptype || [],
            shift: shift || [],
            usergroup: usergroup || [],
            assetreg: assetreg || [],
            lettertype: lettertype || [],
            lettertemp: lettertemp || [],
        };

        setDropdownData(dropdowns);
        setScreenLoader(false)
        setLoading(false)
        setDropdownsLoaded(true);

        return dropdowns; // Return dropdowns for potential use
    }

    // Function to map IDs to names for employee card
    const mapIdsToNamesforEmployeeCard = (employeeData, dropdowns) => {
        if (!employeeData) return {
            title: "",
            first_name: "",
            last_name: "",
            designation_id: {
                id: "",
                designation_name: ""
            },
            id: "",
            profile: null,
        };

        const mappedData = {
            title: employeeData.title || employeeData.personal_info?.title || "",
            first_name: employeeData.first_name || employeeData.personal_info?.first_name || "",
            last_name: employeeData.last_name || employeeData.personal_info?.last_name || "",
            designation_id: {
                id: employeeData.designation_id || employeeData.professional_info?.designation || "",
                designation_name: ""
            },
            id: employeeData.employee_id || employeeData.professional_info?.employee_id || "",
            profile: employeeData.profile || employeeData.professional_info?.profile || null,
        };

        // Map designation_id to designation name
        if (mappedData.designation_id.id && dropdowns.designation.length > 0) {
            const designation = dropdowns.designation.find(desig => desig.id === mappedData.designation_id.id);
            mappedData.designation_id.designation_name = designation?.designation_name || "";
        }

        return mappedData;
    };

    const mapIdsToNamesforContentCard = (employeeData, dropdowns) => {
        if (!employeeData) return employeeData;

        setLoading(true)

        const mappedData = { ...employeeData };

        // Map department_id to department name
        if (mappedData.department_id && dropdowns.department.length > 0) {
            const department = dropdowns.department.find(dept => dept.id === mappedData.department_id);
            mappedData.department_name = department?.department_name || "";
        }

        // Map designation_id to designation name
        if (mappedData.designation_id && dropdowns.designation.length > 0) {
            const designation = dropdowns.designation.find(desig => desig.id === mappedData.designation_id);
            mappedData.designation_name = designation?.designation_name || "";
        }

        // Map reporting_manager_id to reporting manager name
        if (mappedData.reporting_manager_id && dropdowns.empinfo.length > 0) {
            const manager = dropdowns.empinfo.find(emp => emp.id === mappedData.reporting_manager_id);
            mappedData.reporting_manager_name = manager ? `${manager.first_name} ${manager.last_name}` : "";
        }

        // Map employee_group_id to employee group name
        if (mappedData.employee_group_id && dropdowns.empgroup.length > 0) {
            const group = dropdowns.empgroup.find(grp => grp.id === mappedData.employee_group_id);
            mappedData.employee_group_name = group?.employee_group_name || "";
        }

        // Map employee_type to employee type name
        if (mappedData.employee_type && dropdowns.emptype.length > 0) {
            const type = dropdowns.emptype.find(typ => typ.id === mappedData.employee_type);
            mappedData.employee_type_name = type?.employee_type_name || "";
        }

        // Map shift to shift name
        if (mappedData.shift && dropdowns.shift.length > 0) {
            const shift = dropdowns.shift.find(sh => sh.id === mappedData.shift);
            mappedData.shift_name = shift?.shift_name || "";
        }

        // Map user_creation_info user_group_id to user group name
        if (mappedData.user_creation_info?.user_group_id && dropdowns.usergroup.length > 0) {
            const userGroup = dropdowns.usergroup.find(ug => ug.id === mappedData.user_creation_info.user_group_id);
            mappedData.user_creation_info.user_group_name = userGroup?.user_group_name || "";
        }

        // Also map professional_info fields
        if (mappedData.professional_info) {
            // Map professional_info department
            if (mappedData.professional_info.department && dropdowns.department.length > 0) {
                const department = dropdowns.department.find(dept => dept.id === mappedData.professional_info.department);
                mappedData.professional_info.department_name = department?.department_name || "";
            }

            // Map professional_info designation
            if (mappedData.professional_info.designation && dropdowns.designation.length > 0) {
                const designation = dropdowns.designation.find(desig => desig.id === mappedData.professional_info.designation);
                mappedData.professional_info.designation_name = designation?.designation_name || "";
            }

            // Map professional_info reporting_manager
            if (mappedData.professional_info.reporting_manager && dropdowns.empinfo.length > 0) {
                const manager = dropdowns.empinfo.find(emp => emp.id === mappedData.professional_info.reporting_manager);
                mappedData.professional_info.reporting_manager_name = manager ? `${manager.first_name} ${manager.last_name}` : "";
            }

            // Map professional_info employee_group
            if (mappedData.professional_info.employee_group && dropdowns.empgroup.length > 0) {
                const group = dropdowns.empgroup.find(grp => grp.id === mappedData.professional_info.employee_group);
                mappedData.professional_info.employee_group_name = group?.employee_group_name || "";
            }

            // Map professional_info employee_type
            if (mappedData.professional_info.employee_type && dropdowns.emptype.length > 0) {
                const type = dropdowns.emptype.find(typ => Number(typ.id) === Number(mappedData.professional_info.employee_type));
                mappedData.professional_info.employee_type_name =
                    mappedData.professional_info.employee_type_name || type?.employee_type_name || "";
            }

            // Map professional_info shift
            if (mappedData.professional_info.shift && dropdowns.shift.length > 0) {
                const shift = dropdowns.shift.find(sh => sh.id === mappedData.professional_info.shift);
                mappedData.professional_info.shift_name = shift?.shift_name || "";
                mappedData.professional_info.shift_timings_from = shift?.shift_timings_from || mappedData.professional_info.shift_timings_from || null;
                mappedData.professional_info.shift_timings_to = shift?.shift_timings_to || mappedData.professional_info.shift_timings_to || null;
            }
        }

        // Map asset_info array
        if (mappedData.asset_info && Array.isArray(mappedData.asset_info)) {
            mappedData.asset_info = mappedData.asset_info.map(asset => {
                const mappedAsset = { ...asset };

                // Map asset_registry to asset name and details
                if (mappedAsset.asset_registry && dropdowns.assetreg.length > 0) {
                    const assetRegistry = dropdowns.assetreg.find(ar => ar.id === mappedAsset.asset_registry);
                    if (assetRegistry) {
                        mappedAsset.asset_registry_name = assetRegistry.asset_name || "";
                        mappedAsset.asset_id = assetRegistry.asset_id || "";
                        mappedAsset.asset_type = assetRegistry.asset_type_id || "";
                        mappedAsset.category_name = assetRegistry.category_name || "";
                        mappedAsset.sub_category_name = assetRegistry.sub_category_name || "";
                        mappedAsset.condition = assetRegistry.condition || "";
                        mappedAsset.purchase_date = assetRegistry.purchase_date || "";
                        mappedAsset.installation_date = assetRegistry.installation_date || "";
                    }
                }

                // Map handing_over_by employee ID to employee name
                if (mappedAsset.handing_over_by && dropdowns.empinfo.length > 0) {
                    const employee = dropdowns.empinfo.find(emp => emp.id === mappedAsset.handing_over_by);
                    if (employee) {
                        mappedAsset.handing_over_by_name = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
                        mappedAsset.handing_over_by_employee_id = employee.employee_id || "";
                    }
                }

                return mappedAsset;
            });
        }

        return mappedData;
    };

    const getEmployeeDetailsById = async (id) => {
        if (!dropdownsLoaded) {
            ToastMsg('error', 'Dropdowns not loaded yet, skipping employee data fetch');
            return;
        }

        setLoading(true)
        try {
            const apiData = await fetch(EMPLOYEE_INTERN_INFO, { id: id });
            if (apiData?.status === "success" && apiData.data) {
                const employeeData = apiData.data.result[0];

                // Map IDs to names after dropdown data is loaded this set update is only for employee card
                const mappedEmployeeCardData = mapIdsToNamesforEmployeeCard(employeeData, dropdownData);
                const mappedEmployeeData = mapIdsToNamesforContentCard(employeeData, dropdownData);

                setEmpCardDetails(mappedEmployeeCardData);
                setEmployeeContentData(mappedEmployeeData);
            }
        } catch (error) {
            console.error('Error fetching employee details:', error);
            ToastMsg('error', error.message)
        } finally {
            setLoading(false)
        }
    }

    // Initialize all data in sequence
    useEffect(() => {
        const initializeAllData = async () => {
            await fetchDropdown(); // Wait for dropdowns to load first
        };

        initializeAllData();
    }, []);

    // Fetch employee details when dropdowns are loaded and employeeID is available
    useEffect(() => {
        if (dropdownsLoaded && employeeID) {
            // console.log('Dropdowns loaded, fetching employee details...');
            getEmployeeDetailsById(employeeID);
        }
    }, [dropdownsLoaded, employeeID]);

    const [currentStep, setCurrentStep] = useState(0);
    const [visitedSteps, setVisitedSteps] = useState({ 0: true });
    useEffect(() => {
        setVisitedSteps((prev) => (prev[currentStep] ? prev : { ...prev, [currentStep]: true }));
    }, [currentStep]);

    const steps = [
        {
            title: 'Employee Details',
            content: 'EmployeeDetailsContent',
            icon: User
        },
        {
            title: 'Document Details',
            content: 'DocumentDetailsContent',
            icon: FileText
        },
        {
            title: 'Salary Details',
            content: 'SalaryDetailsContent',
            icon: CreditCard
        },
        {
            title: 'Final Verification',
            content: 'FinalVerificationContent',
            icon: Send
        },
    ];

    // First, create handlers for the buttons
    const handleSendMail = async () => {
        if (finalRef.current) {
            const success = await finalRef.current.handleSendMail();
            if (success) {
                // Optional: Do something after successful mail send
                console.log('Mail sent successfully');
            }
        }
    };

    const handleGenerateOfferLetter = async () => {
        if (finalRef.current) {
            const success = await finalRef.current.handleGenerateOfferLetter();
            if (success) {
                // Optional: Do something after successful generation
                console.log('Offer letter generated successfully');
            }
        }
    };

    const handleMoveToEmployee = async () => {
        if (finalRef.current) {
            const success = await finalRef.current.handleMoveToEmployee();
            if (success) {
                // Optional: Do something after successful move
                console.log('Moved to employee list successfully');
            }
        }
    };

    // Update the nextStep function to handle verification properly
    const nextStep = async () => {
        if (currentStep < steps.length - 1) {
            if (currentStep === 1) {
                if (docRef.current) {
                    const success = await docRef.current.handleSaveDocDetails();
                    if (success === false) {
                        return; // Stop if save failed
                    }
                }
            }
            if (currentStep === 2) {
                if (salRef.current) {
                    const success = await salRef.current.handleSaveSalDetails();
                    if (success === false) {
                        return;
                    }
                }
            }
            if (currentStep === 3) {
                if (finalRef.current) {
                    const success = await finalRef.current.handleVerifyDetails();
                    if (success === false) {
                        return;
                    }
                }
            }
            setCurrentStep(currentStep + 1);
        }

        if (currentStep === steps.length - 1) {
            if (finalRef.current) {
                const success = await finalRef.current.handleVerifyDetails();
                if (success === false) {
                    return;
                } else {
                    // Successfully saved, navigate back
                    navigate(-1);
                }
            }
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleBackNavigation = () => {
        navigate(-1);
    }

    const goToStep = (stepIndex) => {
        setCurrentStep(stepIndex);
    };

    const renderStepContent = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                return <EmployeeDetailsContent employeeData={employeeContentData} employeeID={employeeID} />;
            case 1:
                return <DocumentDetailsContent employeeID={employeeID} ref={docRef} />;
            case 2:
                return <SalaryDetailsContent employeeData={employeeContentData} employeeID={employeeID} url={EMPLOYEE_INTERN_SAL} ref={salRef} />;
            case 3:
                return <FinalVerificationContent dropdown={dropdownData} employeeID={employeeID} ref={finalRef} />;
            default:
                return <EmployeeDetailsContent employeeData={employeeContentData} employeeID={employeeID} />;
        }
    };

    if (screenLoader) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <ContainerCard>
            {/* Stepper */}
            <div className="mb-4 shrink-0">
                <div className="flex items-center justify-between relative">
                    {/* Progress line */}
                    <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700 -z-10">
                        <div
                            className="h-full bg-sidebar-primary transition-all duration-300"
                            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                        />
                    </div>

                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        const isCompleted = index < currentStep;
                        const isActive = index === currentStep;
                        const isClickable = index <= currentStep || index === currentStep + 1;

                        // Show employee details for completed steps, title for active/future steps
                        const showEmployeeDetails = isCompleted && index === 0;

                        return (
                            <div key={index} className="flex flex-col items-center relative">
                                {showEmployeeDetails ? (
                                    <div
                                        onClick={() => goToStep(index)}
                                        className="min-w-[100px] lg:min-w-[120px] cursor-pointer px-2"
                                    >
                                        <EmployeeDetails
                                            details={empCardDetails}
                                            size="xs"
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className={cn("flex flex-col lg:flex-row items-center justify-center gap-2 sm:gap-3 min-w-[80px] lg:min-w-[120px] cursor-pointer px-2",
                                            isClickable && "cursor-pointer hover:scale-105 transition-all",
                                        )}
                                        onClick={() => goToStep(index)}
                                    >
                                        <button
                                            className={cn(
                                                "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2 flex-shrink-0",
                                                isCompleted && "bg-sidebar-primary border-sidebar-primary text-white",
                                                isActive && "border-sidebar-primary bg-white dark:bg-gray-800 text-sidebar-primary",
                                                !isActive && !isCompleted && "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-400",
                                            )}
                                        >
                                            <Icon className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                                        </button>
                                        <p className={cn(
                                            "text-xs sm:text-sm font-medium whitespace-nowrap text-center sm:text-left",
                                            isActive && "text-sidebar-primary dark:text-sidebar-primary",
                                            !isActive && "text-gray-500 dark:text-gray-400"
                                        )}>
                                            {step.title}
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Step Content */}
            <div className="flex-1 min-h-0 overflow-y-auto mb-2">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                    {[0, 1, 2, 3].map((index) => (
                        visitedSteps[index] ? (
                            <div key={index} className={index === currentStep ? undefined : "hidden"}>
                                {renderStepContent(index)}
                            </div>
                        ) : null
                    ))}
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center shrink-0 pt-2 border-t border-border">
                <Button
                    variant="outline"
                    onClick={currentStep === 0 ? handleBackNavigation : prevStep}
                    className="flex items-center gap-2"
                    disabled={loading} // Disable during API calls
                >
                    <ChevronLeft className="w-4 h-4" />
                    {currentStep === 0 ? 'Back' : 'Previous'}
                </Button>

                {/* Only show these buttons on the final step */}
                {currentStep === steps.length - 1 && (
                    <>
                        <Button
                            variant="outline"
                            className="flex items-center gap-2"
                            onClick={handleSendMail}
                            disabled={loading}
                        >
                            <Mail className="w-4 h-4" />
                            Send Mail
                        </Button>

                        <Button
                            variant="outline"
                            className="flex items-center gap-2"
                            onClick={handleGenerateOfferLetter}
                            disabled={loading}
                        >
                            <TextQuote className="w-4 h-4" />
                            Generate Offer Letter
                        </Button>

                        <Button
                            variant="outline"
                            className="flex items-center gap-2"
                            onClick={handleMoveToEmployee}
                            disabled={loading}
                        >
                            <Move className="w-4 h-4" />
                            Move To Employee
                        </Button>
                    </>
                )}

                {currentStep !== steps.length - 1 && (
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            Step {currentStep + 1} of {steps.length}
                        </span>
                    </div>
                )}

                <Button
                    variant="outline"
                    onClick={nextStep}
                    variant="brand"
                    className="flex items-center gap-2"
                    disabled={loading}
                >
                    {currentStep === steps.length - 1 ? 'Save & Finish' : 'Next'}
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
        </ContainerCard >
    );
};

export default InternOnboardingViewDetailsIndex;